/**
 * adapters/auth/DevAuthAdapter — ponte de token DEV para o fake-jwks (ADR-0013).
 *
 * Papel: em desenvolvimento local, o browser não dispõe de Firebase. Este adapter
 * implementa `AuthPort` obtendo um JWT assinado pelo stub `fake-jwks` (`POST /token`),
 * de modo que o portal exercite a cadeia REAL browser→gateway→serviço.
 *
 * SEGURANÇA (ADR-0013):
 * - NUNCA deve ser usado em produção. O wiring (AuthProvider) só o seleciona quando
 *   `import.meta.env.DEV` é verdadeiro E a URL do endpoint de token está configurada.
 * - A validação de assinatura RSA permanece ATIVA no backend — não é bypass.
 * - O token vive apenas em memória/sessionStorage (igual ao fluxo Firebase, RNF 1).
 *
 * O token emitido pelo fake-jwks carrega claims `tenant_admin` (sub/tenant_id/role/
 * business_unit_id/memberships) — ver docker/fake-jwks/server.js.
 */

import type {
  AuthPort,
  AuthSession,
  InvitationAcceptance,
  PasswordResetConfirmation,
} from "./AuthPort.js";
import { InvitationError, PasswordResetError } from "./AuthPort.js";

/** Resposta de `POST {tokenUrl}` do fake-jwks. */
interface FakeJwksTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

/** Claims do JWT relevantes para montar a sessão (sub/email). */
interface DevJwtClaims {
  sub?: string;
  email?: string;
}

/** Decodifica o payload de um JWT (base64url) sem verificar assinatura (uso DEV apenas). */
function decodeJwtClaims(token: string): DevJwtClaims {
  const parts = token.split(".");
  if (parts.length < 2) return {};
  try {
    const payload = (parts[1] ?? "").replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(payload);
    return JSON.parse(json) as DevJwtClaims;
  } catch {
    return {};
  }
}

export interface DevAuthAdapterOptions {
  /** URL absoluta do endpoint `POST /token` do fake-jwks (ex.: http://localhost:9000/token). */
  tokenUrl: string;
  /** fetch injetável (default: global) — facilita teste. */
  fetchImpl?: typeof fetch;
}

/**
 * `AuthPort` sobre o fake-jwks. Emite o token uma vez por sessão e o reutiliza;
 * `getIdToken(forceRefresh)` reemite quando forçado (ex.: 401 → renovação).
 */
export class DevAuthAdapter implements AuthPort {
  private readonly tokenUrl: string;
  private readonly fetchImpl: typeof fetch;
  private currentToken: string | null = null;
  private currentSession: AuthSession | null = null;

  constructor(options: DevAuthAdapterOptions) {
    this.tokenUrl = options.tokenUrl;
    this.fetchImpl = options.fetchImpl ?? fetch;
  }

  /** Solicita um JWT ao fake-jwks e atualiza o estado interno. */
  private async issueToken(payload: Record<string, unknown> = {}): Promise<AuthSession> {
    const res = await this.fetchImpl(this.tokenUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error(`fake-jwks token endpoint respondeu ${res.status}`);
    }
    const data = (await res.json()) as FakeJwksTokenResponse;
    const token = data.access_token;
    const claims = decodeJwtClaims(token);
    const session: AuthSession = {
      uid: claims.sub ?? "dev-user",
      email: claims.email ?? "dev@azim.local",
      token,
    };
    this.currentToken = token;
    this.currentSession = session;
    return session;
  }

  /**
   * "Login" DEV: ignora senha e solicita o token padrão `tenant_admin`. O e-mail
   * informado é repassado como claim (rastreabilidade dev), mas os demais claims
   * vêm dos defaults do fake-jwks.
   */
  async signIn(email: string, _password: string): Promise<AuthSession> {
    return this.issueToken(email ? { email } : {});
  }

  /**
   * 2FA não se aplica ao fake-jwks (login DEV é sempre direto). Mantido para satisfazer
   * o contrato `AuthPort`: emite o token padrão como se a verificação tivesse sucedido.
   */
  async verifyOtp(_challengeId: string, _code: string): Promise<AuthSession> {
    return this.issueToken();
  }

  async signOut(): Promise<void> {
    this.currentToken = null;
    this.currentSession = null;
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    if (this.currentToken && !forceRefresh) return this.currentToken;
    if (!this.currentSession && !forceRefresh) return null;
    // Renovação forçada (401) ou sem token em cache: reemite com o mesmo e-mail.
    const session = await this.issueToken(
      this.currentSession?.email ? { email: this.currentSession.email } : {},
    );
    return session.token;
  }

  /**
   * O aceite de convite não tem correspondente no fake-jwks: ele apenas assina tokens, não grava credencial nenhuma.
   * Falhar explicitamente é o contrário de fingir sucesso — quem estiver validando o fluxo de convite localmente precisa saber, na primeira tentativa, que tem de promover a área `session` para a API real (VITE_API_REAL_AREAS) e usar o AzimAuthAdapter.
   */
  async acceptInvitation(
    _token: string,
    _displayName: string,
    _password: string,
  ): Promise<InvitationAcceptance> {
    throw new InvitationError(
      "unknown",
      "O aceite de convite exige o IdP real (AzimAuthAdapter): o fake-jwks não grava credencial.",
    );
  }

  /**
   * No-op DELIBERADO — não é o mesmo caso do defeito que existiu no AzimAuthAdapter.
   *
   * O fake-jwks só assina tokens (`POST /token`); ele não tem noção de usuário
   * cadastrado, credencial ou envio de e-mail, então não existe um serviço real
   * para este método delegar em ambiente DEV. Emitir uma chamada de rede aqui
   * seria fingir uma integração que não existe, não corrigir um defeito.
   * Quem quiser exercitar o fluxo real de redefinição de senha localmente deve
   * promover a área `session` para a API real (VITE_API_REAL_AREAS) e usar o
   * AzimAuthAdapter, que delega para data/repos/authRepo.ts de verdade.
   */
  async requestPasswordReset(_email: string): Promise<void> {
    // no-op deliberado — ver doc acima
  }

  /**
   * Recusa explícita, pelo mesmo motivo do aceite de convite: o fake-jwks só assina tokens, não
   * emite link de redefinição nem grava credencial, então não existe token de redefinição válido a
   * consumir em ambiente DEV.
   * Falhar com uma categoria dizendo isso é melhor do que devolver sucesso: um no-op aqui faria a
   * tela anunciar "senha redefinida" sem senha nenhuma ter mudado, e o defeito só apareceria no
   * próximo login.
   * Para exercitar o fluxo real localmente, aponte o portal para o authentication de verdade.
   */
  async confirmPasswordReset(
    _token: string,
    _password: string,
  ): Promise<PasswordResetConfirmation> {
    throw new PasswordResetError(
      "unknown",
      "A redefinição de senha exige o IdP real (AzimAuthAdapter): o fake-jwks não emite nem consome token de redefinição.",
    );
  }

  /** Sem listener externo de estado em DEV: notifica a sessão corrente (ou nula) uma vez. */
  onAuthStateChanged(callback: (session: AuthSession | null) => void): () => void {
    callback(this.currentSession);
    return () => {};
  }
}

/** Entrada da fábrica: estado de ambiente + URL do token (desacoplado de import.meta). */
export interface DevAuthEnv {
  /** `import.meta.env.DEV` — verdadeiro apenas em build de desenvolvimento. */
  isDev: boolean;
  /** `import.meta.env.PROD` — verdadeiro em build de produção. */
  isProd: boolean;
  /** `import.meta.env.VITE_DEV_AUTH_TOKEN_URL` — endpoint `POST /token` do fake-jwks. */
  tokenUrl: string | undefined;
}

/**
 * Fábrica guardada por ambiente: retorna um `DevAuthAdapter` apenas em DEV (e nunca
 * em produção) e quando `VITE_DEV_AUTH_TOKEN_URL` está definido. Caso contrário
 * retorna `null` — o wiring deve cair para o adapter Firebase. Sem bypass em prod.
 */
export function createDevAuthAdapter(
  env: DevAuthEnv,
  fetchImpl?: typeof fetch,
): DevAuthAdapter | null {
  if (env.isProd || !env.isDev) return null;
  const tokenUrl = env.tokenUrl?.trim();
  if (!tokenUrl) return null;
  return new DevAuthAdapter(
    fetchImpl ? { tokenUrl, fetchImpl } : { tokenUrl },
  );
}
